import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { academyTrackAgencies, academyUpdateAgencyAction } from '../lib/api';
import { ExternalLink, Check, Globe, Building2, Sparkles, Zap, Loader2, ChevronRight, Info } from 'lucide-react';
import SpotifyArtistLookup from './SpotifyArtistLookup';

/** Track → agencies / action steps panel for Music Biz Academy.
 * Displays governing bodies + registries + distributors relevant to the track,
 * with direct sign-up links, API availability, cost info, and a per-user
 * "completed" checkmark so the user can prove they've walked through the process.
 */
export default function AgencyActions({ trackId, color }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setData(await academyTrackAgencies(trackId)); }
    catch { toast.error('Could not load action steps'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [trackId]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return region === 'all' ? data.agencies : data.agencies.filter(a => a.region === region || a.region === 'Global');
  }, [data, region]);

  const toggleDone = async (a) => {
    setBusyId(a.id);
    try {
      await academyUpdateAgencyAction(trackId, a.id, { completed: !a.completed });
      setData(d => ({ ...d,
        agencies: d.agencies.map(x => x.id === a.id ? { ...x, completed: !x.completed, completed_at: !x.completed ? new Date().toISOString() : null } : x),
        completed: d.agencies.reduce((s, x) => s + (x.id === a.id ? (!x.completed ? 1 : 0) : (x.completed ? 1 : 0)), 0),
      }));
      toast.success(a.completed ? 'Marked incomplete' : 'Marked complete ✓');
    } catch { toast.error('Could not update'); }
    finally { setBusyId(null); }
  };

  const regions = useMemo(() => {
    if (!data) return [];
    const s = new Set(data.agencies.map(a => a.region));
    return ['all', ...['Global', 'US', 'UK', 'EU', 'CA', 'AU'].filter(r => s.has(r))];
  }, [data]);

  if (loading) return <div className="flex items-center gap-2 text-white/55 py-6 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading action steps…</div>;
  if (!data || data.total === 0) return null;

  const pct = Math.round((data.completed / data.total) * 100);
  return (
    <section className="mt-6" data-testid={`rc-agencies-${trackId}`}>
      <header className="card-shell p-4 md:p-5 flex items-center gap-4 flex-wrap" style={{ borderColor: (color || '#22d3ee') + '55' }}>
        <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: (color || '#22d3ee') + '22', color }}>
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color }}>{data.completed}/{data.total} actions · {pct}% done</div>
          <h3 className="font-display text-[18px] md:text-[20px] leading-tight mt-0.5">Take action — sign up with the real agencies</h3>
          <p className="text-white/65 text-[12.5px] md:text-[13px] mt-1 max-w-[640px]">Every body, registry, and service relevant to this track. By the end of this course you'll have walked through each step and ticked it off.</p>
        </div>
        <div className="min-w-[120px]">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full transition-all duration-300" style={{ width: pct + '%', background: 'linear-gradient(90deg,#a78bfa,#22d3ee)' }} />
          </div>
        </div>
      </header>

      {regions.length > 2 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {regions.map(r => (
            <button key={r} onClick={() => setRegion(r)} className={`chip text-[11px] ${region === r ? 'active' : ''}`} data-testid={`rc-agency-region-${r}`}>
              {r === 'all' ? 'All regions' : r}
            </button>
          ))}
        </div>
      )}

      <ol className="mt-3 space-y-2.5">
        {filtered.map((a) => (
          <AgencyCard key={a.id} a={a} busy={busyId === a.id} onToggle={() => toggleDone(a)} color={color} />
        ))}
      </ol>
    </section>
  );
}

function AgencyCard({ a, busy, onToggle, color }) {
  const catIcon = a.category === 'pro' ? Building2 : a.category === 'registry' ? Sparkles : a.category === 'distributor' ? ChevronRight : Globe;
  const apiBadgeStyle = {
    'public': { bg: 'rgba(52,211,153,0.14)', fg: '#34d399', border: 'rgba(52,211,153,0.45)' },
    'partner-only': { bg: 'rgba(255,207,82,0.14)', fg: '#ffcf52', border: 'rgba(255,207,82,0.45)' },
    'deep-link': { bg: 'rgba(167,139,250,0.14)', fg: '#a78bfa', border: 'rgba(167,139,250,0.45)' },
    'none': null,
  }[a.api_status] || null;

  const Ic = catIcon;

  return (
    <li className={`card-shell p-4 flex gap-4 flex-col sm:flex-row sm:items-start ${a.completed ? 'ring-1' : ''}`}
        style={a.completed ? { borderColor: (color || '#22d3ee') + '55', '--tw-ring-color': (color || '#22d3ee') + '55' } : undefined}
        data-testid={`rc-agency-${a.id}`}>
      <button
        onClick={onToggle}
        disabled={busy}
        aria-label={a.completed ? 'Mark incomplete' : 'Mark complete'}
        className={`shrink-0 w-10 h-10 rounded-full grid place-items-center border transition-all ${a.completed ? 'border-transparent text-black' : 'border-white/20 text-white/65 hover:border-white/35'}`}
        style={a.completed ? { background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' } : undefined}
        data-testid={`rc-agency-toggle-${a.id}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : a.completed ? <Check className="w-5 h-5" /> : <span className="text-[12px] font-bold">{a.recommended_step}</span>}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 flex items-center gap-1"><Ic className="w-3 h-3" /> {a.category.replace('-', ' ')}</span>
          <span className="chip text-[10px]">{a.region}</span>
          <span className="chip text-[10px] capitalize">{a.cost}{a.cost_detail ? ` · ${a.cost_detail}` : ''}</span>
          {apiBadgeStyle && a.has_api && (
            <span className="chip text-[10px] flex items-center gap-1" style={{ background: apiBadgeStyle.bg, color: apiBadgeStyle.fg, borderColor: apiBadgeStyle.border }}>
              <Zap className="w-3 h-3" /> API · {a.api_status}
            </span>
          )}
        </div>
        <div className="font-display text-[16px] md:text-[17px] mt-1">{a.name}</div>
        <p className="text-white/68 text-[12.5px] md:text-[13px] mt-1 leading-relaxed">{a.description}</p>
        {a.api_note && a.has_api && (
          <p className="text-white/45 text-[11.5px] mt-2 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {a.api_note}</p>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <a href={a.signup_url} target="_blank" rel="noreferrer noopener" className="btn-primary text-[12.5px]" data-testid={`rc-agency-signup-${a.id}`}>
            <ExternalLink className="w-3.5 h-3.5" /> {a.action_label}
          </a>
          {a.signup_url !== a.website && (
            <a href={a.website} target="_blank" rel="noreferrer noopener" className="btn-ghost text-[12.5px]" data-testid={`rc-agency-website-${a.id}`}>
              <Globe className="w-3.5 h-3.5" /> Website
            </a>
          )}
          {a.completed && a.completed_at && (
            <span className="text-[11px] text-white/50 self-center">Completed {new Date(a.completed_at).toLocaleDateString()}</span>
          )}
        </div>
        {a.id === 'spotify-for-artists' && <SpotifyArtistLookup color="#1DB954" />}
      </div>
    </li>
  );
}
